import React from 'react';
import PropTypes from 'prop-types';
import { Collapse as AntCollapse } from 'antd';
import './Collapse.scss';

const { Panel: AntPanel } = AntCollapse;

/**
 * Design System Collapse Component
 *
 * A wrapper around Ant Design Collapse with consistent, scoped styling
 * that is immune to global CSS overrides.
 *
 * Variants:
 * - "default"    — Standard look with light border and proper padding
 * - "borderless" — No outer border, clean separator lines between panels
 * - "ghost"      — Transparent background, minimal chrome
 *
 * @example
 * // Default bordered collapse
 * <Collapse defaultActiveKey={["1"]}>
 *   <Collapse.Panel header="Section 1" key="1">Content</Collapse.Panel>
 *   <Collapse.Panel header="Section 2" key="2">Content</Collapse.Panel>
 * </Collapse>
 *
 * @example
 * // Borderless variant
 * <Collapse variant="borderless" defaultActiveKey={["1"]}>
 *   <Collapse.Panel header="Section 1" key="1">Content</Collapse.Panel>
 * </Collapse>
 *
 * @example
 * // Using items prop (Ant Design 5+ pattern)
 * <Collapse variant="default" items={[
 *   { key: '1', label: 'Section 1', children: <p>Content</p> }
 * ]} />
 */
const Collapse = ({
    variant = 'default',
    className = '',
    children,
    ...props
}) => {
    const variantClass = `ds-collapse-${variant}`;
    const combinedClassName = `ds-collapse ${variantClass} ${className}`.trim();

    // For ghost variant, pass ghost prop to Ant Design
    const isGhost = variant === 'ghost';

    return (
        <AntCollapse
            className={combinedClassName}
            ghost={isGhost}
            {...props}
        >
            {children}
        </AntCollapse>
    );
};

/**
 * Design System Panel — thin wrapper over Ant Design Panel
 * for consistent usage with <Collapse.Panel>.
 */
const Panel = ({ children, ...props }) => (
    <AntPanel {...props}>{children}</AntPanel>
);

Panel.displayName = 'Collapse.Panel';

// Attach Panel as compound component
Collapse.Panel = Panel;

Collapse.propTypes = {
    /** Visual variant */
    variant: PropTypes.oneOf(['default', 'borderless', 'ghost']),

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Panel children or any content */
    children: PropTypes.node,

    /** Keys of initially expanded panels */
    defaultActiveKey: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string
    ]),

    /** Controlled expanded keys */
    activeKey: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string
    ]),

    /** Items config (Ant Design 5+ pattern, alternative to children) */
    items: PropTypes.array,

    /** Whether to allow only one panel open at a time */
    accordion: PropTypes.bool,

    /** Callback when active panels change */
    onChange: PropTypes.func,
};

Panel.propTypes = {
    /** Panel header content */
    header: PropTypes.node,

    /** Unique key for the panel */
    key: PropTypes.string,

    /** Panel children */
    children: PropTypes.node,

    /** Extra content in the panel header corner */
    extra: PropTypes.node,

    /** Whether the panel is collapsible */
    collapsible: PropTypes.oneOf(['header', 'icon', 'disabled']),
};

export default Collapse;
